/**
 * useArtifactGeometryHoverPreview Hook
 *
 * Generic, artifact-aware on-map hover preview for line/shape geometries.
 * Writes to `state.ui.hoveredGeometryPreview` with the appropriate
 * `HoveredMarkerKind` discriminator (`'task' | 'note' | 'obstruction'`).
 *
 * Default `artifactKind = 'task'` preserves legacy behaviour from
 * `useTaskGeometryHoverPreview`.
 *
 * @module hooks/useArtifactGeometryHoverPreview
 */

import { useEffect } from 'react';

import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useGeoMarkupStore } from '@/store';
import type { HoveredMarkerKind } from '@/store/slices/uiSlice';
import type { Coordinates } from '@/types';

export type ArtifactKindForHover = HoveredMarkerKind;

export interface UseArtifactGeometryHoverPreviewOptions {
  artifactKind?: ArtifactKindForHover;
  artifactId: string;
  isHovered: boolean;
  /** Coordinates at which to anchor the preview (e.g. `calculatedCenter`). */
  coordinates: Coordinates | null | undefined;
}

/**
 * Publishes the on-map hover preview for any artifact's line/shape geometry.
 *
 * The store slot (`hoveredGeometryPreview`) is shared with marker hover; the
 * `MarkerHoverPreview` consumer merges both with marker priority.
 */
export function useArtifactGeometryHoverPreview({
  artifactKind = 'task',
  artifactId,
  isHovered,
  coordinates,
}: UseArtifactGeometryHoverPreviewOptions): void {
  const { hasHover } = useDeviceLayout();
  const isDrawing = useGeoMarkupStore((state) => state.isDrawing);
  const setHoveredGeometryPreview = useGeoMarkupStore(
    (state) => state.ui.setHoveredGeometryPreview
  );

  const lat = coordinates?.lat ?? null;
  const lng = coordinates?.lng ?? null;

  useEffect(() => {
    if (!hasHover) return;

    if (isHovered && !isDrawing && lat !== null && lng !== null) {
      setHoveredGeometryPreview({
        kind: artifactKind,
        id: artifactId,
        coordinates: { lat, lng },
      });
      return;
    }

    const current = useGeoMarkupStore.getState().ui.hoveredGeometryPreview;
    if (
      current !== null &&
      current.kind === artifactKind &&
      current.id === artifactId
    ) {
      setHoveredGeometryPreview(null);
    }
  }, [
    hasHover,
    isHovered,
    isDrawing,
    artifactKind,
    artifactId,
    lat,
    lng,
    setHoveredGeometryPreview,
  ]);
}
